import React, { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import scrapingIcon from './scraping-icon.svg';
import chainLinkIcon from './chain-link-4.svg';
import imageAltIcon from './image-alt-text.svg';
import './App.css';

// Validates and normalizes a user input into an absolute http(s) URL
function normalizeUrl(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;

  const isValidAbsoluteHttpUrl = (value) => {
    try {
      const urlObj = new URL(value);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') return false;
      if (urlObj.username || urlObj.password) return false;
      if (urlObj.port) return false;
      return !!urlObj.hostname && !/\s/.test(urlObj.hostname);
    } catch (_) {
      return false;
    }
  };

  if (/^https?:\/\//i.test(trimmed)) {
    return isValidAbsoluteHttpUrl(trimmed) ? trimmed : null;
  }

  const looksLikeHost = (
    /^(?:localhost|(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,})(?::\d{1,5})?(?:\/[^\s]*)?$/i.test(trimmed) ||
    /^(?:\d{1,3}\.){3}\d{1,3}(?::\d{1,5})?(?:\/[^\s]*)?$/i.test(trimmed)
  );

  if (looksLikeHost) {
    const withScheme = `https://${trimmed}`;
    return isValidAbsoluteHttpUrl(withScheme) ? withScheme : null;
  }

  return null;
}

// Generate multiple reasonable URL variants to improve robustness (http/https and optional www)
function generateCandidateUrls(normalizedAbsoluteUrl) {
  return [normalizedAbsoluteUrl];
}

export default function App() {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showError, setShowError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loadTimeMs, setLoadTimeMs] = useState(null);
  const [errorText, setErrorText] = useState('Please enter a valid URL');

  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  const handleRun = async () => {
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setErrorText('Please enter a valid URL');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2500);
      return;
    }
    if (normalized !== url) {
      setUrl(normalized);
    }

    setIsLoading(true);
    setShowResult(false);
    if (imageSrc) setImageSrc(null);
    setLoadTimeMs(null);

    const start = performance.now();
    const candidatePageUrls = generateCandidateUrls(normalized);
    const cacheBuster = `&_=${Date.now()}`;
    const viewportParams = `&width=1920&height=1080`;
    const screenshotUrl = candidatePageUrls.map((pageUrl) => (
      `https://browser-screenshot-demo.evomi.com/?url=${encodeURIComponent(pageUrl)}${viewportParams}${cacheBuster}`
    ))[0];

    let detailedError = 'Could not capture screenshot (timeout or unavailable).';
    let loadedOk = false;

    try {
      const controller = new AbortController();
      const abortId = setTimeout(() => controller.abort(), 60000);
      const res = await fetch(screenshotUrl, {
        method: 'GET',
        signal: controller.signal
      });
      clearTimeout(abortId);
      const elapsed = Math.max(0, performance.now() - start);

      const contentType = (res.headers && res.headers.get('content-type')) || '';
      if (res.ok && contentType.includes('image/')) {
        const imageBlob = await res.blob();
        setImageSrc(URL.createObjectURL(imageBlob));
        setLoadTimeMs(elapsed);
        setShowResult(true);
        loadedOk = true;
      } else if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data && data.error) {
          if (data.error === 'Rate limit exceeded') {
            const limit = data.limit;
            const resetAt = data.resetAt;
            let extra = '';
            if (typeof resetAt === 'number') {
              const seconds = Math.max(0, Math.round((resetAt - Date.now()) / 1000));
              if (seconds) extra = ` Try again in ~${seconds}s.`;
            }
            detailedError = `Rate limit exceeded${typeof limit === 'number' ? ` (limit ${limit})` : ''}.${extra}`;
          } else if (data.error === 'Upstream error') {
            detailedError = `Upstream error${data.status ? ` (${data.status})` : ''}.`;
          } else {
            detailedError = String(data.error);
          }
          // Log full response for debugging
          // eslint-disable-next-line no-console
          console.warn('Screenshot API error:', data);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('Screenshot fetch error:', err);
      }
    }

    setIsLoading(false);
    if (!loadedOk) {
      setShowResult(false);
      setErrorText(detailedError);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRun();
    }
  };

  return (
    <div className="scraping-container">
      <div className="outer-card">

        <div className="header">
          <img
            src={scrapingIcon}
            alt="Scraping Icon"
            className="header-icon-img"
          />
          <h1 className="header-title">
            Scraping Browser Demo
          </h1>
        </div>

        <div className="inner-card">
          <div className="url-label">Enter URL</div>

          <div className="input-container">
            <input
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); if (showError) setShowError(false); }}
              onKeyPress={handleKeyPress}
              placeholder="e.g. evomi.com or https://evomi.com"
              className="url-input"
            />
          </div>

          <button
            onClick={handleRun}
            disabled={isLoading}
            className={`run-button ${showError ? 'error' : ''}`}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Processing...
              </>
            ) : showError ? (
              errorText
            ) : (
              <>
                Run
                <Play size={18} fill="white" />
              </>
            )}
          </button>

          <div className="results-area">
            {isLoading ? (
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading...</div>
              </div>
            ) : showResult ? (
              <div className="success-content">
                {imageSrc ? (
                  <img src={imageSrc} alt="Captured screenshot" className="captured-image" />
                ) : (
                  <div className="success-text">✅ Capture completed successfully</div>
                )}
              </div>
            ) : (
              <div className="placeholder-content">
                <div className="placeholder-icons">
                  <img src={chainLinkIcon} alt="Chain Link" className="placeholder-icon" />
                  <img src={imageAltIcon} alt="Alt text icon" className="placeholder-icon" />
                </div>
                <div className="placeholder-text">
                  <div>Enter a URL to verify</div>
                  <div>Scraping Browser Functionality</div>
                </div>
              </div>
            )}
          </div>

        </div>

        <div className={`footer ${showResult ? 'visible' : ''}`}>
          <span>Load Time: {showResult ? (loadTimeMs ? `${(loadTimeMs / 1000).toFixed(2)}s` : '—') : '—'}</span>
          <span>Delay after capture: 7s</span>

        </div>

      </div>
    </div>
  );
}